import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Flag, X, Send } from "lucide-react";

/**
 * Button + popover for moderators/admins to file a report on a player up
 * to admin. Distinct from peer-to-peer player reports (ReportPlayerButton).
 */
export default function ModReportToAdminButton({ reportedUserId, reportedUsername }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/mod/reports", {
        reported_user_id: reportedUserId,
        reason: reason.trim(),
      });
      toast.success("Report filed to admin");
      setOpen(false);
      setReason("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        data-testid={`mod-report-btn-${reportedUserId}`}
        onClick={() => setOpen((v) => !v)}
        title="Report to admin"
        className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[#EA580C]/60 text-[#EA580C] px-2 py-1 hover:bg-[#EA580C] hover:text-white transition-colors inline-flex items-center gap-1"
      >
        <Flag className="w-3 h-3" /> Report
      </button>
      {open && (
        <div
          data-testid={`mod-report-form-${reportedUserId}`}
          className="absolute right-0 top-full mt-1 w-[280px] bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#EA580C]/60 p-3 z-40 shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#EA580C]">// Report {reportedUsername} to admin</span>
            <button type="button" onClick={() => setOpen(false)} className="text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"><X className="w-3 h-3" /></button>
          </div>
          <form onSubmit={submit}>
            <textarea
              data-testid={`mod-report-reason-${reportedUserId}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Describe the misbehavior (visible to admin)…"
              className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-2 py-1.5 text-xs font-body focus:border-[#EA580C] outline-none text-[#09090B] dark:text-[#F4F4F5] resize-none"
            />
            <button
              type="submit"
              data-testid={`mod-report-submit-${reportedUserId}`}
              disabled={busy || !reason.trim()}
              className="mt-2 w-full bg-[#EA580C] text-white font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 hover:bg-[#C2410C] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1"
            >
              <Send className="w-3 h-3" /> {busy ? "Filing…" : "File Report"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
